import { useMemo, useState, useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ChartCard } from '@/shared/components/dashboard/ChartCard';
import { KpiCard, KpiGrid } from '@/shared/components/dashboard/KpiCard';
import { PageHero } from '@/shared/components/dashboard/PageHero';
import { barOption, donutOption } from '@/shared/components/dashboard/chartOptions';
import { EmptyPanel, ErrorPanel, LoadingPanel } from '@/shared/components/dashboard/panels';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { DataTable } from '@/shared/components/ui/DataTable';
import type { ColumnDef } from '@/shared/components/ui/DataTable';
import { Icons } from '@/shared/components/ui/icons';
import { cn } from '@/shared/lib/cn';
import { useProduct } from '../hooks/useProduct';
import { useUpdateProduct } from '../hooks/useUpdateProduct';
import { useActivateProduct } from '../hooks/useActivateProduct';
import { ProductStatusBadge } from '../components/ProductStatusBadge';
import { ProductForm } from '../components/ProductForm';
import { useProductPlans } from '@/modules/plans/hooks/useProductPlans';
import { useDeletePlan } from '@/modules/plans/hooks/useDeletePlan';
import { PlanCard } from '@/modules/plans/components/PlanCard';
import { listSubscriptions } from '@/modules/subscriptions/services/subscriptions.api';
import type { UpdateProductDTO } from '../types';
import type { SubscriptionResponseDTO, SubscriptionStatus } from '@/modules/subscriptions/types';

interface ProductDetailPageProps {
  productId: string;
}

// Extract dominant color from an image using canvas
function useDominantColor(src: string | null | undefined): string {
  const [color, setColor] = useState<string>('#14b8a6');

  useEffect(() => {
    if (!src) return;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = 16;
        canvas.height = 16;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        ctx.drawImage(img, 0, 0, 16, 16);
        const data = ctx.getImageData(0, 0, 16, 16).data;
        let r = 0,
          g = 0,
          b = 0,
          count = 0;
        for (let i = 0; i < data.length; i += 4) {
          const a = data[i + 3] ?? 0;
          if (a < 128) continue;
          r += data[i] ?? 0;
          g += data[i + 1] ?? 0;
          b += data[i + 2] ?? 0;
          count++;
        }
        if (count === 0) return;
        r = Math.round(r / count);
        g = Math.round(g / count);
        b = Math.round(b / count);
        // Ensure minimum saturation — boost the dominant channel
        const max = Math.max(r, g, b);
        if (max > 0) {
          const factor = 200 / max;
          r = Math.min(255, Math.round(r * factor));
          g = Math.min(255, Math.round(g * factor));
          b = Math.min(255, Math.round(b * factor));
        }
        setColor(`rgb(${r},${g},${b})`);
      } catch {
        // CORS error: keep default
      }
    };
    img.src = src;
  }, [src]);

  return color;
}

function SubStatusBadge({ status }: { status: SubscriptionStatus }) {
  const { t } = useTranslation();
  const colors: Record<SubscriptionStatus, string> = {
    active: 'bg-emerald-100 text-emerald-700',
    trialing: 'bg-blue-100 text-blue-700',
    past_due: 'bg-amber-100 text-amber-700',
    cancelled: 'bg-gray-100 text-gray-600',
    expired: 'bg-rose-100 text-rose-700',
    suspended: 'bg-orange-100 text-orange-700',
  };
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium',
        colors[status] ?? 'bg-muted text-muted-foreground',
      )}
    >
      <span className="h-1.5 w-1.5 rounded-full bg-current opacity-70" />
      {t(`subscriptions.status.${status}`)}
    </span>
  );
}

export function ProductDetailPage({ productId }: ProductDetailPageProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { data: product, isLoading, error } = useProduct(productId);
  const { data: plansData, isLoading: plansLoading } = useProductPlans(productId);
  const { data: subscriptionsData, isLoading: subsLoading } = useQuery({
    queryKey: ['subscriptions', 'by-product', productId],
    queryFn: () => listSubscriptions({ product_id: productId, limit: 200 }),
    enabled: Boolean(productId),
  });
  const toggleActive = useActivateProduct();
  const updateProduct = useUpdateProduct();
  const deletePlan = useDeletePlan();
  const [editOpen, setEditOpen] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [editSuccess, setEditSuccess] = useState<string | null>(null);

  const isAr = i18n.language === 'ar';
  const plans = useMemo(() => plansData?.items ?? [], [plansData?.items]);
  const subs = useMemo(() => subscriptionsData?.items ?? [], [subscriptionsData]);
  const activePlans = plans.filter((plan) => plan.is_active).length;
  const activeSubsCount = subs.filter((s) => s.status === 'active').length;
  const cancelledSubsCount = subs.filter((s) => s.status === 'cancelled').length;
  const trialingCount = subs.filter((s) => s.status === 'trialing').length;
  const tenantSet = new Set(subs.map((s) => s.tenant_id));
  const brandColor = useDominantColor(product?.logo_url);

  const periodOption = useMemo(
    () =>
      donutOption(t('plans.billingPeriod'), [
        {
          name: t('plans.periods.monthly'),
          value: plans.filter((p) => p.billing_period === 'monthly').length,
        },
        {
          name: t('plans.periods.quarterly'),
          value: plans.filter((p) => p.billing_period === 'quarterly').length,
        },
        {
          name: t('plans.periods.yearly'),
          value: plans.filter((p) => p.billing_period === 'yearly').length,
        },
      ]),
    [plans, t],
  );

  const priceOption = useMemo(
    () =>
      barOption(
        t('plans.price'),
        plans.slice(0, 8).map((p) => p.slug),
        plans.slice(0, 8).map((p) => p.price_amount),
        true,
      ),
    [plans, t],
  );

  const subColumns: ColumnDef<SubscriptionResponseDTO>[] = useMemo(
    () => [
      {
        key: 'tenant',
        header: t('subscriptions.tenant'),
        accessor: (s) => (
          <div className="flex items-center gap-2">
            <span className="flex h-7 w-7 items-center justify-center rounded-md bg-muted">
              <Icons.Building className="h-3.5 w-3.5 text-muted-foreground" />
            </span>
            <span className="font-mono text-xs text-muted-foreground">
              {s.tenant_id.slice(0, 12)}…
            </span>
          </div>
        ),
        sortValue: (s) => s.tenant_id,
      },
      {
        key: 'plan',
        header: t('products.detail.plan'),
        accessor: (s) => (
          <span className="font-mono text-xs text-muted-foreground">{s.plan_id.slice(0, 8)}…</span>
        ),
        sortValue: (s) => s.plan_id,
        align: 'center',
      },
      {
        key: 'status',
        header: t('common.status'),
        accessor: (s) => <SubStatusBadge status={s.status} />,
        sortValue: (s) => s.status,
        align: 'center',
      },
      {
        key: 'started_at',
        header: t('products.detail.started'),
        accessor: (s) => (
          <span className="text-xs text-muted-foreground">
            {new Date(s.started_at).toLocaleDateString()}
          </span>
        ),
        sortValue: (s) => s.started_at,
        align: 'center',
      },
      {
        key: 'current_period_end',
        header: t('products.detail.renewal'),
        accessor: (s) => (
          <div className="text-center">
            <p className="text-xs text-muted-foreground">
              {new Date(s.current_period_end).toLocaleDateString()}
            </p>
            <p
              className={cn(
                'text-xs font-medium',
                s.status === 'cancelled' || s.status === 'expired'
                  ? 'text-rose-500'
                  : 'text-emerald-600',
              )}
            >
              {(() => {
                const diff = Math.ceil(
                  (new Date(s.current_period_end).getTime() - Date.now()) / 86400000,
                );
                if (diff < 0) return t('products.detail.daysAgo', { count: Math.abs(diff) });
                if (diff === 0) return t('products.detail.today');
                return t('products.detail.daysLeft', { count: diff });
              })()}
            </p>
          </div>
        ),
        sortValue: (s) => s.current_period_end,
        align: 'center',
      },
      {
        key: 'auto_renew',
        header: t('products.detail.autoRenew'),
        accessor: (s) =>
          s.auto_renew ? (
            <span className="flex items-center justify-center gap-1 text-xs text-emerald-600">
              <Icons.RefreshCw className="h-3 w-3" /> {t('products.detail.on')}
            </span>
          ) : (
            <span className="text-xs text-muted-foreground">{t('products.detail.off')}</span>
          ),
        sortValue: (s) => (s.auto_renew ? 1 : 0),
        align: 'center',
      },
    ],
    [t],
  );

  const subscriptionStatusFilters = useMemo(
    () =>
      (
        [
          'active',
          'trialing',
          'past_due',
          'cancelled',
          'expired',
          'suspended',
        ] as SubscriptionStatus[]
      ).map((status) => ({
        label: t(`subscriptions.status.${status}`),
        value: status,
      })),
    [t],
  );

  if (isLoading) return <LoadingPanel />;
  if (error || !product) return <ErrorPanel message={t('products.notFound')} />;

  const name = isAr ? product.name_ar : product.name_en;

  const handleUpdateProduct = async (data: UpdateProductDTO) => {
    setEditError(null);
    setEditSuccess(null);
    try {
      await updateProduct.mutateAsync({ id: product.id, data });
      setEditSuccess(t('products.updateSuccess'));
      setEditOpen(false);
    } catch (err) {
      setEditError(err instanceof Error ? err.message : t('common.error'));
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Brand-colored hero */}
      <PageHero
        title={name}
        subtitle={product.description_en ?? t('dashboard.modules.products')}
        icon={
          product.logo_url ? (
            <img src={product.logo_url} alt={name} className="h-8 w-8 rounded-md object-cover" />
          ) : (
            <Icons.Package className="h-7 w-7" />
          )
        }
        eyebrow={product.slug}
        metrics={[
          {
            label: t('products.isActive'),
            value: product.is_active ? t('products.status.active') : t('products.status.inactive'),
          },
          { label: t('plans.title'), value: String(plans.length) },
          { label: t('plans.isActive'), value: String(activePlans) },
          { label: t('products.detail.brand'), value: brandColor },
        ]}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <ProductStatusBadge isActive={product.is_active} />
            <Button
              variant="outline"
              size="sm"
              isLoading={toggleActive.isPending}
              onClick={() => toggleActive.mutate({ id: product.id, activate: !product.is_active })}
            >
              {product.is_active
                ? t('products.actions.deactivate')
                : t('products.actions.activate')}
            </Button>
            <Button
              variant={editOpen ? 'default' : 'outline'}
              size="sm"
              onClick={() => setEditOpen((o) => !o)}
            >
              {editOpen ? (
                <>
                  <Icons.X className="me-1 h-3.5 w-3.5" /> {t('products.detail.close')}
                </>
              ) : (
                <>
                  <Icons.Pencil className="me-1 h-3.5 w-3.5" /> {t('products.edit')}
                </>
              )}
            </Button>
          </div>
        }
      />

      {/* Edit product form */}
      {editOpen && (
        <Card
          style={{ borderColor: brandColor, boxShadow: `0 0 0 1px ${brandColor}20` }}
          className="transition-all"
        >
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <span className="inline-flex" style={{ color: brandColor }}>
                <Icons.Pencil className="h-4 w-4" />
              </span>
              {t('products.detail.editProduct')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ProductForm
              mode="edit"
              defaultValues={{
                slug: product.slug,
                name_en: product.name_en,
                name_ar: product.name_ar,
                description_en: product.description_en,
                redirect_url: product.redirect_url,
                logo_url: product.logo_url,
              }}
              onSubmit={handleUpdateProduct as never}
              isSubmitting={updateProduct.isPending}
              submitError={editError}
              submitSuccess={editSuccess}
            />
          </CardContent>
        </Card>
      )}

      {/* Logo + brand colors showcase */}
      {product.logo_url && (
        <div
          className="flex items-center gap-6 rounded-xl border p-6"
          style={{
            borderColor: `${brandColor}40`,
            background: `linear-gradient(135deg, ${brandColor}08 0%, ${brandColor}03 100%)`,
          }}
        >
          <img
            src={product.logo_url}
            alt={name}
            className="h-20 w-20 rounded-xl object-cover shadow-md"
            style={{ boxShadow: `0 4px 24px ${brandColor}30` }}
          />
          <div>
            <p className="font-semibold text-foreground text-lg">{name}</p>
            <p className="text-xs text-muted-foreground mt-1">{product.slug}</p>
            <div className="flex items-center gap-2 mt-3">
              <div
                className="h-4 w-4 rounded-full border border-white/30 shadow"
                style={{ background: brandColor }}
              />
              <span className="text-xs font-mono text-muted-foreground">{brandColor}</span>
              <span className="text-xs text-muted-foreground">
                — {t('products.detail.brandColor')}
              </span>
            </div>
          </div>
        </div>
      )}

      <KpiGrid>
        <KpiCard
          title={t('plans.title')}
          value={String(plans.length)}
          icon={<Icons.Layers className="h-4 w-4" />}
          tone="blue"
        />
        <KpiCard
          title={t('plans.isActive')}
          value={String(activePlans)}
          icon={<Icons.CheckCircle className="h-4 w-4" />}
          tone="green"
        />
        <KpiCard
          title={t('products.detail.activeSubscribers')}
          value={String(activeSubsCount)}
          icon={<Icons.Users className="h-4 w-4" />}
          tone="cyan"
        />
        <KpiCard
          title={t('products.detail.tenantsUsing')}
          value={String(tenantSet.size)}
          icon={<Icons.Building className="h-4 w-4" />}
          tone="violet"
        />
      </KpiGrid>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <ChartCard
          title={t('plans.billingPeriod')}
          subtitle={t('plans.title')}
          option={periodOption}
        />
        <ChartCard title={t('plans.price')} subtitle={t('products.slug')} option={priceOption} />
      </div>

      {/* Product details */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {t('products.name')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-foreground">{product.name_en}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {t('products.nameAr')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-foreground" dir="rtl">
              {product.name_ar}
            </p>
          </CardContent>
        </Card>
        {product.description_en && (
          <Card className="md:col-span-2">
            <CardHeader>
              <CardTitle className="text-sm font-medium text-muted-foreground">
                {t('products.description')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-foreground">{product.description_en}</p>
            </CardContent>
          </Card>
        )}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {t('products.platformUrl')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <a
              href={product.redirect_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline text-sm truncate block"
            >
              {product.redirect_url}
            </a>
          </CardContent>
        </Card>
      </div>

      {/* Plans management */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
            <Icons.Layers className="h-5 w-5 text-primary" />
            {t('plans.title')}
          </h2>
          <Button
            size="sm"
            onClick={() =>
              void navigate({
                to: '/dashboard/plans/new',
                search: { productId: product.id },
              })
            }
          >
            <Icons.Plus className="me-1.5 h-3.5 w-3.5" />
            {t('plans.create')}
          </Button>
        </div>

        {plansLoading ? (
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        ) : plans.length === 0 ? (
          <EmptyPanel title={t('plans.notFound')} message={t('dashboard.modules.plans')} />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {plans.map((plan) => (
              <PlanCard
                key={plan.id}
                plan={plan}
                showActions
                onDelete={(id) => deletePlan.mutate(id)}
                isDeleting={deletePlan.isPending && deletePlan.variables === plan.id}
              />
            ))}
          </div>
        )}
      </div>

      {/* Product Statistics — Subscriptions Table */}
      <div className="space-y-3">
        <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
          <Icons.BarChart className="h-5 w-5 text-primary" />
          {t('products.detail.productStatistics')}
        </h2>

        {/* Quick stats row */}
        <KpiGrid>
          <KpiCard
            title={t('products.detail.totalSubscriptions')}
            value={String(subs.length)}
            icon={<Icons.Repeat className="h-4 w-4" />}
            tone="blue"
            hint={t('products.detail.allTimeAcrossPlans')}
          />
          <KpiCard
            title={t('subscriptions.status.active')}
            value={String(activeSubsCount)}
            icon={<Icons.CheckCircle className="h-4 w-4" />}
            tone="green"
          />
          <KpiCard
            title={t('subscriptions.status.trialing')}
            value={String(trialingCount)}
            icon={<Icons.Timer className="h-4 w-4" />}
            tone="cyan"
          />
          <KpiCard
            title={t('subscriptions.status.cancelled')}
            value={String(cancelledSubsCount)}
            icon={<Icons.XCircle className="h-4 w-4" />}
            tone="rose"
          />
        </KpiGrid>

        {subsLoading ? (
          <LoadingPanel />
        ) : (
          <DataTable
            data={subs}
            columns={subColumns}
            rowKey={(s) => s.id}
            selectable
            initialPageSize={10}
            emptyMessage={t('products.detail.noSubscriptions')}
            filters={[
              {
                key: 'status',
                label: t('common.status'),
                type: 'select',
                options: subscriptionStatusFilters,
              },
            ]}
          />
        )}
      </div>
    </div>
  );
}
